/* =============================================================
   Celebrities Look — infinite carousel + Quick View
   Prefix: cl-
   ============================================================= */
(function () {
  'use strict';

  /* =========================================================================
     BUY-NOW ABANDON CLEANUP (runs on every page load, before section init)
     -------------------------------------------------------------------------
     Mirrors the working ZWC QuickView flow. A true "Buy It Now" must not
     leave the single item in the cart if the shopper abandons GoKwik.
     GoKwik's popup is a cross-origin iframe, so we cannot detect its close
     from the parent page reliably. Instead we use a durable flag:
       • On Buy It Now: store the original-cart snapshot + a flag in
         sessionStorage, THEN open GoKwik.
       • If the shopper completes the order, GoKwik lands them on an order /
         thank-you page — we detect that and CLEAR the flag (keep purchase).
       • If the shopper abandons and returns to any store page, the flag is
         still set → we restore the original cart (removing the Buy-Now
         item) and clear the flag.
     Uses the SAME key as zwc-quickview.js so the two scripts never fight —
     whichever runs first performs the cleanup; the flag removal is
     synchronous so the second script sees nothing to do.
     ========================================================================= */
  var ZWC_BUYNOW_KEY = 'zwcBuyNowPending';
  var ORDER_URL_HINTS = ['/thank', 'thank_you', '/orders/', 'order-confirm', 'checkout/thank'];

  function pathLooksLikeOrder() {
    var href = (window.location.href || '').toLowerCase();
    return ORDER_URL_HINTS.some(function (h) { return href.indexOf(h) !== -1; });
  }

  function runBuyNowCleanup() {
    var raw;
    try { raw = sessionStorage.getItem(ZWC_BUYNOW_KEY); } catch (e) { raw = null; }
    if (!raw) return;

    if (pathLooksLikeOrder()) {
      try { sessionStorage.removeItem(ZWC_BUYNOW_KEY); } catch (e) {}
      return;
    }

    var data;
    try { data = JSON.parse(raw); } catch (e) { data = null; }
    try { sessionStorage.removeItem(ZWC_BUYNOW_KEY); } catch (e) {}
    if (!data) return;

    var snapshot = data.snapshot || [];
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function () {
        if (!snapshot.length) return;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: snapshot })
        });
      })
      .then(function () {
        ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (ev) {
          try { document.dispatchEvent(new CustomEvent(ev, { bubbles: true })); } catch (e) {}
        });
      })
      .catch(function () { /* best effort */ });
  }

  runBuyNowCleanup();

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ── CAROUSEL INIT ─────────────────────────────────── */

  function initSection(section) {
    var blockCount = parseInt(section.dataset.blockCount, 10) || 0;
    if (blockCount === 0) return;

    var cardsDesktop  = parseInt(section.dataset.cardsDesktop, 10)  || 4;
    var cardsMobile   = parseInt(section.dataset.cardsMobile, 10)   || 2;
    var hoverInterval = parseInt(section.dataset.hoverInterval, 10) || 800;
    var viewersCount  = parseInt(section.dataset.viewers, 10)       || 0;
    var cardFocus     = section.dataset.cardFocus !== 'false';

    var heroTrack  = section.querySelector('.cl-hero-track');
    var cardsTrack = section.querySelector('.cl-cards-track');
    var dots       = section.querySelectorAll('.cl-dot');
    var cards      = section.querySelectorAll('.cl-card');

    var currentIndex = blockCount;
    var isAnimating  = false;
    var snapTimer    = null;

    function setPosition(idx, animate) {
      currentIndex = idx;
      if (!animate) {
        heroTrack.style.transition  = 'none';
        cardsTrack.style.transition = 'none';
      }
      section.style.setProperty('--cl-current', idx);
      if (!animate) {
        void cardsTrack.getBoundingClientRect();
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            heroTrack.style.transition  = '';
            cardsTrack.style.transition = '';
          });
        });
      }
      updateDots();
      if (cardFocus) updateFocus();
    }

    function snapIfNeeded() {
      clearTimeout(snapTimer);
      if (currentIndex >= blockCount * 2) {
        setPosition(currentIndex - blockCount, false);
      } else if (currentIndex < blockCount) {
        setPosition(currentIndex + blockCount, false);
      }
    }

    function onTransitionEnd(e) {
      if (e.target !== cardsTrack) return;
      if (e.propertyName !== 'transform') return;
      clearTimeout(snapTimer);
      isAnimating = false;
      snapIfNeeded();
    }

    cardsTrack.addEventListener('transitionend', onTransitionEnd);

    function startFallback() {
      clearTimeout(snapTimer);
      snapTimer = setTimeout(function () {
        if (isAnimating) { isAnimating = false; snapIfNeeded(); }
      }, 750);
    }

    function nextSlide() {
      if (isAnimating) return;
      isAnimating = true; startFallback();
      setPosition(currentIndex + 1, true);
    }

    function prevSlide() {
      if (isAnimating) return;
      isAnimating = true; startFallback();
      setPosition(currentIndex - 1, true);
    }

    function goTo(logicalIdx) {
      if (isAnimating) return;
      isAnimating = true; startFallback();
      setPosition(blockCount + logicalIdx, true);
    }

    section.querySelectorAll('.cl-prev').forEach(function (b) { b.addEventListener('click', prevSlide); });
    section.querySelectorAll('.cl-next').forEach(function (b) { b.addEventListener('click', nextSlide); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { goTo(i); }); });

    function updateDots() {
      var logical = ((currentIndex % blockCount) + blockCount) % blockCount;
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === logical); });
    }

    function updateFocus() {
      var logical = ((currentIndex % blockCount) + blockCount) % blockCount;
      cards.forEach(function (card) {
        var idx = parseInt(card.dataset.blockIndex, 10);
        card.classList.toggle('is-focus', idx === logical);
      });
    }

    var tStartX = 0, tStartY = 0, tActive = false;

    function onTouchStart(e) {
      tStartX = e.touches[0].clientX;
      tStartY = e.touches[0].clientY;
      tActive = true;
    }

    function onTouchEnd(e) {
      if (!tActive) return;
      tActive = false;
      var dx = e.changedTouches[0].clientX - tStartX;
      var dy = e.changedTouches[0].clientY - tStartY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) nextSlide(); else prevSlide();
      }
    }

    [section.querySelector('.cl-cards-viewport'), section.querySelector('.cl-hero-viewport')]
      .forEach(function (el) {
        if (!el) return;
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchend', onTouchEnd);
      });

    /* ── Desktop card click → navigate to product page ── */
    cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        /* Only navigate on desktop (> 749px) */
        if (window.innerWidth <= 749) return;
        /* Do not navigate when the eye/quick-view button was clicked */
        if (e.target.closest('.cl-eye')) return;
        /* Do not navigate when the card-info <a> was clicked — it handles itself */
        if (e.target.closest('.cl-card-info')) return;
        var url = card.dataset.productUrl;
        if (url) window.location.href = url;
      });
    });

    cards.forEach(function (card) {
      var imgs    = card.querySelectorAll('.cl-card-img');
      var imgDots = card.querySelectorAll('.cl-card-img-dot');
      if (imgs.length <= 1) return;

      var idx = 0, timer = null;

      function show(i) {
        imgs.forEach(function (img, n)  { img.classList.toggle('is-active', n === i); });
        imgDots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
      }

      function startCycle() {
        stopCycle();
        timer = setInterval(function () { idx = (idx + 1) % imgs.length; show(idx); }, hoverInterval);
      }

      function stopCycle() { if (timer) { clearInterval(timer); timer = null; } }

      var media = card.querySelector('.cl-card-media');
      if (!media) return;
      media.addEventListener('mouseenter', startCycle);
      media.addEventListener('mouseleave', function () { stopCycle(); idx = 0; show(0); });
    });

    initQuickView(section, viewersCount);
    setPosition(currentIndex, false);
  }

  /* ── QUICK VIEW ────────────────────────────────────── */

  function initQuickView(section, viewersCount) {
    var sectionId = section.dataset.sectionId;
    var dataNode  = section.querySelector('script[data-cl-products="' + sectionId + '"]');
    if (!dataNode) return;

    var products;
    try { products = JSON.parse(dataNode.textContent); }
    catch (e) { console.error('[celebrities-look] product JSON parse error', e); return; }

    var byBlock = {};
    products.forEach(function (p) { byBlock[p.blockId] = p; });

    var payDataNode = section.querySelector('script[data-cl-pay="' + sectionId + '"]');
    var paymentImgUrl = '';
    if (payDataNode) { try { paymentImgUrl = JSON.parse(payDataNode.textContent) || ''; } catch (e) {} }

    var sizeChartUrl = section.dataset.sizeChart || '';
    var customBuyUrl = section.dataset.buyUrl    || '';

    /* ── View Details button settings ── */
    var vdShow      = section.dataset.vdShow !== 'false';
    var vdLabel     = section.dataset.vdLabel || 'VIEW DETAILS';
    var vdShowArrow = section.dataset.vdArrow !== 'false';

    var modal    = section.querySelector('.cl-qv');
    var body     = modal.querySelector('[data-cl-qv-body]');
    var closeEls = modal.querySelectorAll('[data-cl-qv-close]');

    var qvState = { product: null, imageIdx: 0, variant: null, qty: 1 };

    /* ── Size Chart popup ─────────────────────────────── */
    var scModal = section.querySelector('.cl-sc');

    function openSizeChart() {
      if (!scModal) return;
      var contentDiv = scModal.querySelector('[data-cl-sc-content]');
      if (contentDiv && !contentDiv.dataset.loaded && sizeChartUrl) {
        contentDiv.innerHTML = '<div class="cl-sc-loading">Loading&hellip;</div>';

        /* FIX: the size chart page is a two-file architecture —
             • page description → table markup + CSS-only IN/CM toggle (<style>)
             • Custom Liquid block/section → JS companion (Shopify's page
               editor strips <script> from page content, so it lives there)
           The old code stripped every <style>/<script> tag and dropped any
           non-"main" section, which killed the toggle CSS (→ both units
           rendered concatenated, e.g. "22.2"+"8.7" = "22.28.7") and the JS.
           The ?sections=main-page fast path can't work here either: it
           returns a single section (missing a Custom Liquid section) and
           innerHTML never executes <script> tags anyway. So: fetch the full
           page, keep the template's own sections and their <style> tags,
           and re-create the content's <script> tags so they execute. */
        fetch(sizeChartUrl)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = (new DOMParser()).parseFromString(html, 'text/html');

            /* Drop theme chrome, keep the page template's own sections.
               OS 2.0 ids: header/footer/announcement groups render as
               shopify-section-sections--… while template sections (main
               page content AND any Custom Liquid section on the template)
               render as shopify-section-template--…                       */
            doc.querySelectorAll('[id^="shopify-section-"]').forEach(function (el) {
              var id = el.id || '';
              var keep;
              if (id.indexOf('template--') !== -1) {
                keep = !/header|footer|announcement/.test(id);
              } else if (id.indexOf('sections--') !== -1) {
                keep = false;
              } else {
                /* vintage themes without template-- ids */
                keep = /main|page|content|article/.test(id);
              }
              if (!keep) el.parentNode && el.parentNode.removeChild(el);
            });

            ['header', 'footer', 'nav', 'noscript'].forEach(function (tag) {
              doc.querySelectorAll(tag).forEach(function (el) {
                el.parentNode && el.parentNode.removeChild(el);
              });
            });

            var main = doc.querySelector('main, #MainContent, #main-content, [role="main"]') || doc.body;

            /* Lift executable scripts out BEFORE injection — <script> tags
               inserted via innerHTML are inert by spec, so they must be
               re-created as real elements afterwards. Only scripts inside
               the page's main content are taken (the unit-toggle JS
               companion), never the theme's global scripts. JSON blobs
               (type="application/json" etc.) are left in place untouched. */
            var scripts = [];
            main.querySelectorAll('script').forEach(function (s) {
              var type = (s.getAttribute('type') || '').toLowerCase();
              if (type && type !== 'text/javascript' && type !== 'module') return;
              scripts.push({
                src:  s.getAttribute('src') || '',
                text: s.textContent || '',
                type: type
              });
              s.parentNode && s.parentNode.removeChild(s);
            });

            /* <style> tags (and any stylesheet <link>s) inside the content
               are kept intact — this is what powers the IN/CM toggle. */
            contentDiv.innerHTML = main.innerHTML;

            scripts.forEach(function (s) {
              var tag = document.createElement('script');
              if (s.type) tag.type = s.type;
              if (s.src) { tag.src = s.src; } else { tag.textContent = s.text; }
              contentDiv.appendChild(tag);
            });

            contentDiv.dataset.loaded = 'true';
          })
          .catch(function () {
            contentDiv.innerHTML = '<p style="padding:16px">Could not load size chart. <a href="' + escapeHtml(sizeChartUrl) + '" target="_blank" rel="noopener">Open in new tab →</a></p>';
          });
      }
      scModal.classList.add('is-open');
      scModal.setAttribute('aria-hidden', 'false');
    }

    function closeSizeChart() {
      if (!scModal) return;
      scModal.classList.remove('is-open');
      scModal.setAttribute('aria-hidden', 'true');
    }

    if (scModal) {
      scModal.querySelectorAll('[data-cl-sc-close]').forEach(function (el) {
        el.addEventListener('click', closeSizeChart);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && scModal.classList.contains('is-open')) closeSizeChart();
      });
    }

    /* ── QV helpers ───────────────────────────────────── */

    function pickSizeOption(product) {
      if (!product.options || !product.options.length) return null;
      return product.options.find(function (o) { return /size/i.test(o.name); }) || product.options[0];
    }

    function findVariant(product, optPos, value) {
      var key = 'option' + optPos;
      return product.variants.find(function (v) { return v[key] === value && v.available; })
        || product.variants.find(function (v) { return v[key] === value; });
    }

    function isValueAvailable(product, optPos, value) {
      var key = 'option' + optPos;
      return product.variants.some(function (v) { return v[key] === value && v.available; });
    }

    function openModal(blockId) {
      var p = byBlock[blockId];
      if (!p) return;
      qvState.product  = p;
      qvState.imageIdx = 0;
      qvState.qty      = 1;
      qvState.variant  = p.variants.find(function (v) { return v.available; }) || p.variants[0] || null;
      buildBody();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    /* ── View Details href sync ─────────────────────────
       Keeps the product-page link in step with the currently selected
       variant so the PDP opens pre-selected. Called after build and on
       every size change.                                              */
    function syncViewDetailsHref() {
      var a = body.querySelector('[data-cl-viewdetails]');
      if (!a || !qvState.product) return;
      var base = qvState.product.url || '';
      if (!base) return;
      a.href = qvState.variant
        ? base + (base.indexOf('?') !== -1 ? '&' : '?') + 'variant=' + qvState.variant.id
        : base;
    }

    var scrollThumbIntoView = function () {};

    /* ── Infinite slide-based image navigation ──────────
       Strategy: clone first image after last, clone last image before first.
       Real images occupy slots 1 … N; slot 0 = clone of last; slot N+1 = clone of first.
       Translate to slot (imageIdx + 1), then snap silently on transitionend.
    ─────────────────────────────────────────────────── */

    var qvImgTotal     = 0;   // real image count
    var qvImgAnimating = false;
    var qvSnapTimer    = null;

    function showImage(i) {
      if (qvImgTotal === 0) return;
      // Wrap logical index
      qvState.imageIdx = ((i % qvImgTotal) + qvImgTotal) % qvImgTotal;

      var track = body.querySelector('.cl-qv-main-track');
      if (!track) return;

      // Physical slot = logical index + 1 (because slot 0 is the tail clone)
      var physicalSlot = qvState.imageIdx + 1;
      track.style.transition = 'transform 0.45s cubic-bezier(0.65, 0, 0.35, 1)';
      track.style.transform  = 'translateX(-' + (physicalSlot * 100) + '%)';

      // Store physical slot on the track so transitionend can snap if needed
      track.dataset.physicalSlot = physicalSlot;

      updateQvThumbs();
      scrollThumbIntoView(qvState.imageIdx);
    }

    function showImageInstant(physicalSlot) {
      var track = body.querySelector('.cl-qv-main-track');
      if (!track) return;
      track.style.transition = 'none';
      track.style.transform  = 'translateX(-' + (physicalSlot * 100) + '%)';
      track.dataset.physicalSlot = physicalSlot;
      void track.getBoundingClientRect();
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (track) track.style.transition = 'transform 0.45s cubic-bezier(0.65, 0, 0.35, 1)';
        });
      });
    }

    function onQvTrackTransitionEnd(e) {
      if (e.propertyName !== 'transform') return;
      clearTimeout(qvSnapTimer);
      qvImgAnimating = false;
      var track = body.querySelector('.cl-qv-main-track');
      if (!track) return;
      var slot = parseInt(track.dataset.physicalSlot, 10);
      // Snap from tail clone (slot 0) → real last (slot N)
      if (slot === 0) {
        showImageInstant(qvImgTotal);          // slot N = real last
        qvState.imageIdx = qvImgTotal - 1;
        updateQvThumbs();
      }
      // Snap from head clone (slot N+1) → real first (slot 1)
      else if (slot === qvImgTotal + 1) {
        showImageInstant(1);                   // slot 1 = real first
        qvState.imageIdx = 0;
        updateQvThumbs();
      }
    }

    function updateQvThumbs() {
      body.querySelectorAll('.cl-qv-thumb').forEach(function (el, n) {
        el.classList.toggle('is-active', n === qvState.imageIdx);
      });
    }

    /* ── Build modal body ─────────────────────────────── */

    function buildBody() {
      var p       = qvState.product;
      var sizeOpt = pickSizeOption(p);
      var optPos  = sizeOpt ? sizeOpt.position : 1;

      qvImgTotal     = p.images.length;
      qvImgAnimating = false;

      /* Build cloned infinite track:
         [clone-of-last] [img0] [img1] … [imgN-1] [clone-of-first]
         Each slide is 100% wide; we translateX by physicalSlot * -100%.
      */
      var trackSlides = '';
      if (qvImgTotal > 0) {
        // Tail clone (clone of last image, sits at physical slot 0)
        trackSlides += '<img class="cl-qv-main-img" src="' + escapeHtml(p.images[qvImgTotal - 1]) + '" alt="" aria-hidden="true" />';
        // Real images (physical slots 1 … N)
        p.images.forEach(function (src, i) {
          trackSlides += '<img class="cl-qv-main-img" src="' + escapeHtml(src) + '" alt="' + escapeHtml(p.title) + '" />';
        });
        // Head clone (clone of first image, sits at physical slot N+1)
        trackSlides += '<img class="cl-qv-main-img" src="' + escapeHtml(p.images[0]) + '" alt="" aria-hidden="true" />';
      }

      // Initial translate lands on slot 1 (real first image)
      var imgsHtml = '<div class="cl-qv-main-track" data-qv-track data-physical-slot="1" style="transform: translateX(-100%); transition: transform 0.45s cubic-bezier(0.65, 0, 0.35, 1);">'
        + trackSlides
        + '</div>';

      var thumbsHtml = p.images.map(function (src, i) {
        return '<button type="button" class="cl-qv-thumb' + (i === 0 ? ' is-active' : '')
          + '" data-idx="' + i + '" aria-label="Image ' + (i + 1) + '">'
          + '<img src="' + escapeHtml(src) + '" alt="" /></button>';
      }).join('');

      var arrowsHtml = qvImgTotal > 1
        ? '<button type="button" class="cl-qv-arrow cl-qv-arrow-prev" data-qv-prev aria-label="Previous image">'
          + '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg></button>'
          + '<button type="button" class="cl-qv-arrow cl-qv-arrow-next" data-qv-next aria-label="Next image">'
          + '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg></button>'
        : '';

      var sizesHtml = '';
      if (sizeOpt) {
        sizesHtml = sizeOpt.values.map(function (val) {
          var avail = isValueAvailable(p, optPos, val);
          var isAct = qvState.variant && qvState.variant['option' + optPos] === val;
          return '<button type="button" class="cl-qv-size'
            + (isAct ? ' is-active' : '') + (avail ? '' : ' is-unavailable')
            + '" data-value="' + escapeHtml(val) + '">' + escapeHtml(val) + '</button>';
        }).join('');
      }

      var viewingHtml = viewersCount > 0
        ? '<div class="cl-qv-viewing"><span class="cl-qv-viewing-dot"></span>'
          + viewersCount + ' people are viewing this right now</div>'
        : '';

      var paymentsHtml = '<div class="cl-qv-payments">';
      if (paymentImgUrl) {
        paymentsHtml += '<img class="cl-pay-img-single" src="' + escapeHtml(paymentImgUrl) + '" alt="Payment methods" loading="lazy" />';
      } else {
        paymentsHtml += '<span class="cl-pay">PhonePe</span><span class="cl-pay">GPay</span>'
          + '<span class="cl-pay">Paytm</span><span class="cl-pay">RuPay</span>'
          + '<span class="cl-pay">VISA</span><span class="cl-pay">COD</span>';
      }
      paymentsHtml += '</div>';

      /* ── View Details button ───────────────────────────
         Rendered as a real anchor so middle-click / open-in-new-tab work.
         href is set immediately and kept in sync by syncViewDetailsHref(). */
      var viewDetailsHtml = '';
      if (vdShow && p.url) {
        viewDetailsHtml = '<a class="cl-qv-viewdetails" data-cl-viewdetails href="' + escapeHtml(p.url) + '">'
          + '<span>' + escapeHtml(vdLabel) + '</span>'
          + (vdShowArrow
            ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>'
            : '')
          + '</a>';
      }

      var thumbStripHtml = qvImgTotal > 1
        ? '<div class="cl-qv-thumbs-wrap">'
          + '<button type="button" class="cl-qv-thumb-arr cl-qv-thumb-prev" data-qv-thumb-prev aria-label="Previous images">'
          + '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>'
          + '</button>'
          + '<div class="cl-qv-thumbs-viewport">'
          + '<div class="cl-qv-thumbs" data-qv-thumbs>' + thumbsHtml + '</div>'
          + '</div>'
          + '<button type="button" class="cl-qv-thumb-arr cl-qv-thumb-next" data-qv-thumb-next aria-label="Next images">'
          + '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>'
          + '</button>'
          + '</div>'
        : '';

      body.innerHTML =
        '<div class="cl-qv-gallery">'
        + '<div class="cl-qv-main">' + arrowsHtml + imgsHtml + '</div>'
        + thumbStripHtml
        + '</div>'
        + '<div class="cl-qv-info">'
        + '<h2 class="cl-qv-title">' + escapeHtml(p.title) + '</h2>'
        + '<div class="cl-qv-price" data-qv-price>' + (qvState.variant ? qvState.variant.priceFormatted : p.priceFormatted) + '</div>'
        + '<div class="cl-qv-tax">Tax included. Shipping calculated at checkout.</div>'
        + (sizeOpt
          ? '<div class="cl-qv-row"><div class="cl-qv-row-head"><span class="cl-qv-label">SELECT '
            + escapeHtml(sizeOpt.name).toUpperCase() + '</span>'
            + (sizeChartUrl ? '<button type="button" class="cl-qv-size-chart" data-qv-size-chart>Size Chart</button>' : '')
            + '</div>'
            + '<div class="cl-qv-sizes" data-qv-sizes data-opt-pos="' + optPos + '">' + sizesHtml + '</div></div>'
          : '')
        + '<div class="cl-qv-row"><span class="cl-qv-label">QUANTITY</span>'
        + '<div class="cl-qv-qty">'
        + '<button type="button" data-qv-minus aria-label="Decrease quantity">−</button>'
        + '<input type="number" data-qv-qty value="1" min="1" />'
        + '<button type="button" data-qv-plus aria-label="Increase quantity">+</button>'
        + '</div></div>'
        + '<div class="cl-qv-actions">'
        + '<button type="button" class="cl-qv-atc" data-qv-atc>ADD TO CART</button>'
        + '<button type="button" class="cl-qv-buy" data-qv-buy>'
        + '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18"/></svg>'
        + 'BUY IT NOW</button></div>'
        + viewingHtml
        + paymentsHtml
        + viewDetailsHtml
        + '</div>';

      // Attach transitionend for infinite snap
      var track = body.querySelector('[data-qv-track]');
      if (track) {
        track.addEventListener('transitionend', onQvTrackTransitionEnd);
        // Fallback snap in case transitionend doesn't fire (e.g. reduced-motion)
        track.addEventListener('transitionend', function () {
          clearTimeout(qvSnapTimer);
        });
      }

      attachModalHandlers(p, optPos);
      syncAvailability();
      syncViewDetailsHref();
    }

    function syncAvailability() {
      var atc   = body.querySelector('[data-qv-atc]');
      var buy   = body.querySelector('[data-qv-buy]');
      var price = body.querySelector('[data-qv-price]');
      if (!qvState.variant) {
        if (atc) atc.disabled = true;
        if (buy) buy.disabled = true;
        return;
      }
      if (price) price.innerHTML = qvState.variant.priceFormatted || qvState.product.priceFormatted;
      var avail = qvState.variant.available;
      if (atc) { atc.disabled = !avail; atc.textContent = avail ? 'ADD TO CART' : 'SOLD OUT'; }
      if (buy) buy.disabled = !avail;
    }

    function attachModalHandlers(p, optPos) {
      var total = p.images.length;

      var prevImg = body.querySelector('[data-qv-prev]');
      var nextImg = body.querySelector('[data-qv-next]');
      if (prevImg) prevImg.addEventListener('click', function () { showImage(qvState.imageIdx - 1); });
      if (nextImg) nextImg.addEventListener('click', function () { showImage(qvState.imageIdx + 1); });

      // Swipe support on the main image area
      var mainEl = body.querySelector('.cl-qv-main');
      if (mainEl) {
        var swipeStartX = 0, swipeStartY = 0;
        mainEl.addEventListener('touchstart', function (e) {
          swipeStartX = e.touches[0].clientX;
          swipeStartY = e.touches[0].clientY;
        }, { passive: true });
        mainEl.addEventListener('touchend', function (e) {
          var dx = e.changedTouches[0].clientX - swipeStartX;
          var dy = e.changedTouches[0].clientY - swipeStartY;
          if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy)) {
            if (dx < 0) showImage(qvState.imageIdx + 1);
            else        showImage(qvState.imageIdx - 1);
          }
        });
      }

      body.querySelectorAll('.cl-qv-thumb').forEach(function (t) {
        t.addEventListener('click', function () { showImage(parseInt(t.dataset.idx, 10)); });
      });

      var thumbsViewport = body.querySelector('.cl-qv-thumbs-viewport');
      var thumbsTrack    = body.querySelector('[data-qv-thumbs]');
      var thumbPrevBtn   = body.querySelector('[data-qv-thumb-prev]');
      var thumbNextBtn   = body.querySelector('[data-qv-thumb-next]');
      var thumbOffset    = 0;

      function getThumbLayout() {
        var thumb = body.querySelector('.cl-qv-thumb');
        if (!thumb) return { step: 80, vis: 5, max: Math.max(0, total - 5) };
        var thumbW = thumb.offsetWidth || 70;
        var gap    = 10;
        if (thumbsTrack) {
          var cs = window.getComputedStyle(thumbsTrack);
          var g  = parseFloat(cs.gap || cs.columnGap);
          if (!isNaN(g) && g >= 0) gap = g;
        }
        var step = thumbW + gap;
        var vpW  = thumbsViewport ? (thumbsViewport.offsetWidth || 0) : 0;
        var vis  = vpW > 0 ? Math.max(1, Math.floor((vpW + gap) / step)) : 5;
        return { step: step, vis: vis, max: Math.max(0, total - vis) };
      }

      function syncThumbNav() {
        var t = getThumbLayout();
        thumbOffset = Math.min(thumbOffset, t.max);
        if (thumbsTrack) thumbsTrack.style.transform = 'translateX(-' + (thumbOffset * t.step) + 'px)';
        var needNav = total > t.vis;
        if (thumbPrevBtn) { thumbPrevBtn.disabled = thumbOffset <= 0;     thumbPrevBtn.style.display = needNav ? '' : 'none'; }
        if (thumbNextBtn) { thumbNextBtn.disabled = thumbOffset >= t.max; thumbNextBtn.style.display = needNav ? '' : 'none'; }
      }

      scrollThumbIntoView = function (i) {
        var t = getThumbLayout();
        if (i < thumbOffset) thumbOffset = i;
        else if (i >= thumbOffset + t.vis) thumbOffset = i - t.vis + 1;
        thumbOffset = Math.min(t.max, Math.max(0, thumbOffset));
        syncThumbNav();
      };

      if (thumbPrevBtn) { thumbPrevBtn.addEventListener('click', function () { thumbOffset = Math.max(0, thumbOffset - 1); syncThumbNav(); }); }
      if (thumbNextBtn) { thumbNextBtn.addEventListener('click', function () { thumbOffset = Math.min(getThumbLayout().max, thumbOffset + 1); syncThumbNav(); }); }

      requestAnimationFrame(function () { syncThumbNav(); });

      var sizeChartBtn = body.querySelector('[data-qv-size-chart]');
      if (sizeChartBtn && sizeChartUrl) { sizeChartBtn.addEventListener('click', openSizeChart); }

      var sizesWrap = body.querySelector('[data-qv-sizes]');
      if (sizesWrap) {
        sizesWrap.querySelectorAll('.cl-qv-size').forEach(function (b) {
          b.addEventListener('click', function () {
            if (b.classList.contains('is-unavailable')) return;
            sizesWrap.querySelectorAll('.cl-qv-size').forEach(function (x) { x.classList.remove('is-active'); });
            b.classList.add('is-active');
            var v = findVariant(p, optPos, b.dataset.value);
            if (v) qvState.variant = v;
            syncAvailability();
            syncViewDetailsHref();
          });
        });
      }

      var qtyInput = body.querySelector('[data-qv-qty]');
      var minus    = body.querySelector('[data-qv-minus]');
      var plus     = body.querySelector('[data-qv-plus]');

      function setQty(n) { n = Math.max(1, n); qvState.qty = n; if (qtyInput) qtyInput.value = n; }
      if (minus)    minus.addEventListener('click',    function ()  { setQty(qvState.qty - 1); });
      if (plus)     plus.addEventListener('click',     function ()  { setQty(qvState.qty + 1); });
      if (qtyInput) qtyInput.addEventListener('change', function () { var v = parseInt(qtyInput.value, 10); setQty(isNaN(v) ? 1 : v); });

      var atcBtn = body.querySelector('[data-qv-atc]');
      var buyBtn = body.querySelector('[data-qv-buy]');
      if (atcBtn) atcBtn.addEventListener('click', function () { doAddToCart(atcBtn); });
      if (buyBtn) buyBtn.addEventListener('click', function () { doBuyNow(buyBtn); });

      /* View Details — restore body scroll before navigating away so a
         bfcache restore doesn't return to a locked page. Plain-click only:
         modifier / middle clicks keep native new-tab behaviour. */
      var vdBtn = body.querySelector('[data-cl-viewdetails]');
      if (vdBtn) {
        vdBtn.addEventListener('click', function (e) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          document.body.style.overflow = '';
        });
      }
    }

    /* =====================================================================
       CART PLUMBING — ported from the working ZWC QuickView
       ===================================================================== */

    function snapshotCart() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          return (cart.items || []).map(function (it) {
            return { id: it.variant_id, quantity: it.quantity, properties: it.properties || {} };
          });
        })
        .catch(function () { return []; });
    }

    function replaceCartWith(variantId, qty) {
      return fetch('/cart/clear.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (!cart || !cart.item_count) throw new Error('cart still empty');
            return cart;
          });
      });
    }

    function restoreCart(snapshot) {
      return fetch('/cart/clear.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      })
      .then(function () {
        if (!snapshot || !snapshot.length) return;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: snapshot })
        });
      })
      .then(function () { pokeCartChanged(); })
      .catch(function () { /* best effort */ });
    }

    function pokeCartChanged() {
      ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (ev) {
        try { document.dispatchEvent(new CustomEvent(ev, { bubbles: true })); } catch (e) {}
      });
    }

    function waitForGokwikAndClick(onReady, onGaveUp) {
      var attempts = 0;
      var maxAttempts = 20;
      var interval = 200;
      (function poll() {
        var btn = document.getElementById('gokwik-buy-now') ||
                  document.querySelector('.gokwik-checkout button');
        var enabled = btn && !btn.disabled && !btn.classList.contains('disabled');
        if (enabled) { onReady(btn); return; }
        attempts++;
        if (attempts >= maxAttempts) { if (onGaveUp) onGaveUp(); return; }
        setTimeout(poll, interval);
      })();
    }

    function drawerLooksOpen(el) {
      if (!el) return false;
      return el.hasAttribute('open') ||
             el.classList.contains('is-open') ||
             el.classList.contains('active') ||
             el.getAttribute('aria-hidden') === 'false';
    }

    function openCartDrawerOrNotify() {
      // Prestige (and Maestrooo themes generally) attach their cart listeners
      // to document.documentElement — events dispatched on `document` never
      // pass through it, so they must be fired on documentElement directly.
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return null; })
        .then(function (cart) {
          // Prestige v7: SidebarCart listens for cart:refresh on
          // documentElement; openMiniCart:true makes the theme re-render
          // AND open the drawer itself in one shot.
          try {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { cart: cart, openMiniCart: true }
            }));
          } catch (e) {}
          // Prestige v8 / Impact-style: the cart-drawer web component
          // re-renders on cart:change.
          try {
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: { cart: cart, baseEvent: 'variant:add' }
            }));
          } catch (e) {}
          try {
            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart: cart }
            }));
          } catch (e) {}

          // Give the theme a beat to re-render, then force-open only if the
          // events alone didn't open it (avoids toggling it closed again).
          setTimeout(function () {
            var drawer = document.getElementById('cart-drawer') ||
                         document.querySelector('cart-drawer') ||
                         document.getElementById('sidebar-cart') ||
                         document.querySelector('#CartDrawer, .drawer--cart');
            if (drawerLooksOpen(drawer)) return;

            if (drawer && typeof drawer.show === 'function') {
              try { drawer.show(); return; } catch (e) {}
            }
            if (drawer && 'open' in drawer) {
              try {
                drawer.open = true;
                if (drawerLooksOpen(drawer)) return;
              } catch (e) {}
            }

            var opener = document.querySelector(
              '[aria-controls="cart-drawer"], ' +
              '[data-action="open-drawer"][data-drawer-id="sidebar-cart"], ' +
              '[data-cart-drawer-open], .js-cart-drawer-open, #cart-icon-bubble'
            );
            if (opener) { try { opener.click(); return; } catch (e) {} }

            if (drawer) drawer.setAttribute('open', '');
          }, 350);
        });
    }

    /* ── ADD TO CART (opens the theme cart drawer, Prestige-safe) ── */
    function doAddToCart(btn) {
      if (!qvState.variant || !qvState.variant.available) return;
      var orig = btn.textContent;
      btn.classList.add('is-loading');
      btn.textContent = 'ADDING…';

      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';

      var cartSections = [];
      ['cart-drawer', 'cart-notification', 'cart-icon-bubble'].forEach(function (id) {
        if (document.getElementById('shopify-section-' + id)) cartSections.push(id);
      });

      var payload = { id: qvState.variant.id, quantity: qvState.qty };
      if (cartSections.length) payload.sections = cartSections;

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          btn.classList.remove('is-loading');
          if (!res.ok) {
            btn.textContent = 'ERROR — TRY AGAIN';
            setTimeout(function () { btn.textContent = orig; }, 2000);
            return;
          }

          if (res.data.sections) {
            Object.keys(res.data.sections).forEach(function (id) {
              var el = document.getElementById('shopify-section-' + id);
              if (!el) return;
              var tmp = document.createElement('div');
              tmp.innerHTML = res.data.sections[id];
              var inner = tmp.querySelector('[id^="shopify-section-"]') || tmp.firstElementChild;
              if (inner) el.innerHTML = inner.innerHTML;
            });
          }

          btn.textContent = 'ADDED ✓';
          closeModal();
          openCartDrawerOrNotify();
          setTimeout(function () { btn.textContent = orig; }, 900);
        })
        .catch(function () {
          btn.classList.remove('is-loading');
          btn.textContent = 'ERROR — TRY AGAIN';
          setTimeout(function () { btn.textContent = orig; }, 2000);
        });
    }

    /* ── BUY IT NOW (GoKwik, ported from the working ZWC QuickView) ──
       1. Snapshot original cart + persist a pending-abandon flag BEFORE
          opening GoKwik. If the shopper abandons, the flag survives the
          popup close (and even a page navigation) and cleanup runs on the
          next store page load. A completed order lands on a thank-you URL,
          where cleanup is skipped and the flag cleared.
       2. Replace cart with ONLY the selected variant (popup shows the right
          product; GoKwik also needs a non-empty cart to initialise).
       3. Open the initialised #gokwik-buy-now / .gokwik-checkout button.
       4. If GoKwik never becomes ready: clear the flag, restore the cart,
          then fall back to the custom Buy URL (if set) or native checkout. */
    function doBuyNow(btn) {
      if (!qvState.variant || !qvState.variant.available) return;
      btn.disabled = true;
      var orig = btn.textContent;
      btn.classList.add('is-loading');
      btn.textContent = 'PROCESSING…';
      var vId = qvState.variant.id, vQty = qvState.qty;

      snapshotCart().then(function (snapshot) {
        // Persist the abandon flag BEFORE we touch the cart / open GoKwik.
        try {
          sessionStorage.setItem(ZWC_BUYNOW_KEY, JSON.stringify({
            snapshot: snapshot,
            variantId: vId,
            ts: Date.now()
          }));
        } catch (e) {}

        return replaceCartWith(vId, vQty).then(function () {
          pokeCartChanged();
          waitForGokwikAndClick(function onReady(gkBtn) {
            gkBtn.click();
            setTimeout(function () {
              btn.classList.remove('is-loading');
              btn.textContent = orig;
              btn.disabled = false;
            }, 2500);
          }, function onGaveUp() {
            // GoKwik never opened. Clear the flag (we're not abandoning a
            // popup), restore cart, use the fallback URL / native checkout.
            try { sessionStorage.removeItem(ZWC_BUYNOW_KEY); } catch (e) {}
            restoreCart(snapshot).then(function () {
              if (customBuyUrl) { window.location.href = customBuyUrl; return; }
              window.location.href = '/checkout';
            });
          });
        });
      }).catch(function () {
        try { sessionStorage.removeItem(ZWC_BUYNOW_KEY); } catch (e) {}
        btn.classList.remove('is-loading');
        btn.textContent = 'ERROR — TRY AGAIN';
        setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, 2000);
      });
    }

    section.addEventListener('click', function (e) {
      var eye = e.target.closest('.cl-eye');
      if (!eye || !section.contains(eye)) return;
      e.preventDefault();
      if (eye.dataset.blockId) openModal(eye.dataset.blockId);
    });

    closeEls.forEach(function (el) { el.addEventListener('click', closeModal); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
  }

  /* ── BOOT ──────────────────────────────────────────── */

  function boot() {
    document.querySelectorAll('.cl-wrapper').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('.cl-wrapper');
    if (s) initSection(s);
  });

})();